import {Renderer} from "../utils/Renderer.ts";
import {Anchor} from "../types.ts";
import {getParent} from "../utils/PureElementUtils.ts";

enum MessageType {
    INIT = "INIT",
    UPDATE_ANCHORS = "UPDATE_ANCHORS",
    TRAVERSE_GEOMETRY_TREE = "TRAVERSE_GEOMETRY_TREE",
    UPDATE_GEOMETRY = "UPDATE_GEOMETRY",
    UPDATE_GEOMETRY_BATCH = "UPDATE_GEOMETRY_BATCH",
    UPDATE_LAYOUT = "UPDATE_LAYOUT",
    UPDATE_DIMENSIONS = "UPDATE_DIMENSIONS",
    UPDATE_SRC = "UPDATE_SRC",
    UPDATE_SRC_BATCH = "UPDATE_SRC_BATCH",
    ADD_FONT = "ADD_FONT",
    RENDER_ACK = "RENDER_ACK",
    DELETE_ITEMS = "DELETE_ITEMS",
}

export type Layout = { layout_id: string };
export type Dimensions = { width: number, height: number };
export type ItemData = {
    id: string, geometry: Geometry, hide_flags: HideFlags, public_hide_flags: HideFlags,
    // What the MAIN thread is believed to have committed for x/y/width/height, mirroring
    // public_text / public_hide_flags. `geometry` is the worker's scratch space — it is
    // overwritten by this tick's own math, so it cannot double as the record of what was
    // published. Emits diff against this; see Renderer.addUpdate.
    public_geometry?: GeometryCopy,
    children: string[], auto_size: string, view_type: string, anchor: Anchor, type: string,
    // see docs/EXTRACTED_COMMENTS.md#c264
    text: string, public_text?: string, font: string,
    measuredWidth?: number,
    measuredHeight?: number,
    /* see docs/EXTRACTED_COMMENTS.md#c700 */
    textAdvance?: number,
    /** The exact string textAdvance was measured for. Never use one without checking the other. */
    textAdvanceFor?: string,
    omit_duplicate?: boolean,
    // see docs/EXTRACTED_COMMENTS.md#c265
    auto_hide?: string,
    // Layout "overlay": neither hides nor is hidden by an overlapping sibling.
    overlay?: boolean,
    // Main-thread "no data" verdict (the HIDE_WHEN_NO_DATA condition), shipped as an INPUT so the
    // renderer can compose HIDE_WHEN_NO_DATA_AND_NO_CHILDREN without reading main-staged flags.
    no_data?: boolean,
    // Mirrors Component.flags.template_resolved: an unresolved container must not be
    // children-hidden (same guard getNewState used to apply on the main thread).
    template_resolved?: boolean,
    /* see docs/EXTRACTED_COMMENTS.md#c679 */
    variable_rows?: boolean,
};
export type EvalOrderResult =
    | { ok: true; order: string[] }
    | { ok: false; reason: "cycle_detected" | "multiple_parents" | "unknown_id"; details?: string[] };

/* see docs/EXTRACTED_COMMENTS.md#c792 */
export type Geometry = {
    position: string,
    x: number,
    y: number,
    height: number,
    width: number,
    borderX: number,
    borderY: number,
    original: GeometryCopy
};
export type HideFlags = { data: boolean, render: boolean, alternating: boolean, rule: boolean, image: boolean, anchoring: boolean, dedup: boolean, playlist: boolean, countdown: boolean, timer: boolean, children: boolean };
export type GeometryCopy = { x: number, y: number, height: number, width: number };
/* see docs/EXTRACTED_COMMENTS.md#c800 */
export type RenderEntry = [string, any];
type AnchorData = { anchors: Object };

type Task = {
    id: number; messageType: MessageType,
    item?: ItemData, anchors?: AnchorData,
    layout?: Layout, dimensions?: Dimensions
};


/** --- Render Worker - Completely Independent --- */
let renderer: Renderer;
export const itemData: Map<string, ItemData> = new Map();
export const anchors: Map<string, string[]> = new Map();

// see docs/EXTRACTED_COMMENTS.md#c266
let acceptedVersion = 0;

let renderBusy = false;
let renderAgain = false;
/* see docs/EXTRACTED_COMMENTS.md#c698 */
let pendingRequestId = 0;
/** Highest requestId a pass has claimed; a higher pendingRequestId means an awaiter is waiting. */
let lastServedRequestId = 0;
let renderTimeout: any = null;
let renderTimeoutDue = 0;
let awaitingRenderAck = false;
let lastRenderBatchId = 0;

const RENDER_COALESCE_MS = 16;
let renderCoalesceTimer: any = null;
let lastGeometryAt = 0;

/* see docs/EXTRACTED_COMMENTS.md#c793 */
export const pendingGeomIds: Set<string> = new Set();
const stagedText: Map<string, string> = new Map();
// see docs/EXTRACTED_COMMENTS.md#c267
const latestText: Map<string, string> = new Map();
/* see docs/EXTRACTED_COMMENTS.md#c700 */
const latestTextWidth: Map<string, { text: string; width: number }> = new Map();

function scheduleRender() {
    lastGeometryAt = performance.now();

    if (renderCoalesceTimer != null) return;
    renderCoalesceTimer = setTimeout(() => {
        renderCoalesceTimer = null;

        const sinceLast = performance.now() - lastGeometryAt;
        if (sinceLast < RENDER_COALESCE_MS && pendingGeomIds.size > 0) {
            scheduleRender();
            return;
        }

        renderFlushBatched();
    }, RENDER_COALESCE_MS);
}

/**
 * A render request (TRAVERSE_GEOMETRY_TREE) runs its pass without the coalesce windows. The main
 * thread posts that request's pending geometry and src ahead of it on this same channel, so they
 * are already ingested and waiting gathers nothing for it — the windows were pure latency on every
 * render-gated queue iteration. The 0ms timer still lets messages already queued behind it in.
 */
function renderRequested() {
    if (renderCoalesceTimer != null) {
        clearTimeout(renderCoalesceTimer);
        renderCoalesceTimer = null;
    }
    renderFlushBatched(0);
}

/** Returns whether the item's text changed (and a render is needed). */
function applySrcUpdate(update: { id?: string, src?: string, textWidth?: number }): boolean {
    const id = update.id;
    if (!id) return false;
    const src = update.src ?? "";
    latestText.set(id, src);
    /* see docs/EXTRACTED_COMMENTS.md#c700 */
    const textWidth = typeof update.textWidth === "number" ? update.textWidth : undefined;
    if (textWidth === undefined) latestTextWidth.delete(id);
    else latestTextWidth.set(id, { text: src, width: textWidth });
    const existing = itemData.get(id);
    if (existing) {
        existing.textAdvance = textWidth;
        existing.textAdvanceFor = textWidth === undefined ? undefined : src;
        if (existing.text === src) return false;
        // see docs/EXTRACTED_COMMENTS.md#c625
        existing.text = src;
    } else {
        stagedText.set(id, src);
    }
    return true;
}

function ingestItem(item: ItemData) {
    const prev = itemData.get(item.id);
    // see docs/EXTRACTED_COMMENTS.md#c791
    if (!prev
        || prev.anchor?.anchor_id !== item.anchor?.anchor_id
        || prev.omit_duplicate !== item.omit_duplicate
        || prev.type !== item.type) {
        renderer?.markStructureDirty();
    }
    // see docs/EXTRACTED_COMMENTS.md#c268
    const desired = latestText.get(item.id);
    if (desired !== undefined) {
        item.text = desired;
    } else if (item.text != null) {
        latestText.set(item.id, item.text);
    }
    /* see docs/EXTRACTED_COMMENTS.md#c700 */
    const measured = latestTextWidth.get(item.id);
    if (measured !== undefined) {
        item.textAdvance = measured.width;
        item.textAdvanceFor = measured.text;
    }
    itemData.set(item.id, item);
    if (stagedText.has(item.id)) {
        const staged = stagedText.get(item.id)!;
        item.text = staged;
        latestText.set(item.id, staged);
        stagedText.delete(item.id);
    }
    // see docs/EXTRACTED_COMMENTS.md#c793
    for (let id: string | null = item.id; id != null; id = getParent(id)) {
        pendingGeomIds.add(id);
    }
}

self.onmessage = (e: MessageEvent<Task | any>) => {
    if (!e.data) return;

    switch (e.data.messageType) {
        case MessageType.INIT: {
            renderer = new Renderer();
            renderer.updateItems(itemData);
            (self as DedicatedWorkerGlobalScope).postMessage({ type: "RENDER_READY" });
            return;
        }
        case MessageType.UPDATE_ANCHORS: {
            if (!e.data.anchors || !e.data.anchors.anchors) return;
            anchors.clear();
            for (let [key, value] of Object.entries(e.data.anchors.anchors)) {
                anchors.set(key, value as string[]);
            }
            renderer.updateAnchors(anchors);
            scheduleRender();
            break;
        }
        case MessageType.UPDATE_GEOMETRY: {
            if (!e.data.item) return;
            if (e.data.id < acceptedVersion) return;
            ingestItem(e.data.item as ItemData);
            break;
        }
        case MessageType.UPDATE_GEOMETRY_BATCH: {
            if (e.data.id < acceptedVersion) return;
            const items = e.data.items as ItemData[] | undefined;
            if (!items || items.length === 0) return;
            for (const item of items) {
                ingestItem(item);
            }
            scheduleRender();
            break;
        }
        case MessageType.UPDATE_SRC: {
            if (e.data.id < acceptedVersion) return;
            if (applySrcUpdate(e.data.srcUpdate ?? {})) scheduleRender();
            break;
        }
        case MessageType.UPDATE_SRC_BATCH: {
            if (e.data.id < acceptedVersion) return;
            const updates = (e.data.srcUpdates ?? []) as Array<{ id?: string, src?: string, textWidth?: number }>;
            let changed = false;
            for (const update of updates) {
                if (applySrcUpdate(update)) changed = true;
            }
            if (changed) scheduleRender();
            break;
        }
        case MessageType.TRAVERSE_GEOMETRY_TREE: {
            if (e.data.id < acceptedVersion) return;
            // see docs/EXTRACTED_COMMENTS.md#c698
            if (typeof e.data.requestId === "number" && e.data.requestId > pendingRequestId) {
                pendingRequestId = e.data.requestId;
            }
            renderRequested();
            break;
        }
        case MessageType.UPDATE_LAYOUT: {
            if (!e.data.layout) return;
            acceptedVersion = e.data.id ?? 0;
            renderer.setLayout(e.data.layout.layout_id);
            itemData.clear();
            pendingGeomIds.clear();
            latestText.clear();
            latestTextWidth.clear();
            stagedText.clear();
            renderer.updateItems(itemData);
            // see docs/EXTRACTED_COMMENTS.md#c626
            break;
        }
        case MessageType.ADD_FONT: {
            if (!e.data.font) return;
            const font = e.data.font;
            // see docs/EXTRACTED_COMMENTS.md#c627
            const face = new FontFace(font.name, font.buffer, font.descriptors);
            face.load()
                .then(() => {
                    self.fonts.add(face);
                    // see docs/EXTRACTED_COMMENTS.md#c628
                    (self as DedicatedWorkerGlobalScope).postMessage({
                        type: "FONT_READY",
                        family: font.name,
                    });
                })
                .catch((err) => {
                    (self as DedicatedWorkerGlobalScope).postMessage({
                        type: "FONT_READY",
                        family: font.name,
                        error: `${err}`,
                    });
                });
            break;
        }
        case MessageType.UPDATE_DIMENSIONS: {
            if (!e.data.dimensions) return;
            const {width, height} = e.data.dimensions;
            renderer.setDimensions(width, height);
            break;
        }
        case MessageType.DELETE_ITEMS: {
            const ids = e.data.ids as string[] | undefined;
            if (!ids || ids.length === 0) return;
            for (const id of ids) {
                // see docs/EXTRACTED_COMMENTS.md#c629
                itemData.delete(id);
                pendingGeomIds.delete(id);
                latestText.delete(id);
                latestTextWidth.delete(id);
                stagedText.delete(id);
            }
            renderer.deleteItems(ids);
            scheduleRender();
            break;
        }
        case MessageType.RENDER_ACK: {
            awaitingRenderAck = false;
            if (renderAgain) {
                renderAgain = false;
                // An awaiter the busy pass could not serve runs next without the windows too.
                if (pendingRequestId > lastServedRequestId) renderRequested();
                else scheduleRender();
            }
            break;
        }
    }
}

/** --- Batched render flush --- */
function renderFlushBatched(delayMs: number = RENDER_COALESCE_MS) {
    if (renderBusy || awaitingRenderAck) {
        renderAgain = true;
        return;
    }
    if (itemData.size == 0) return;

    // Never push an already-armed earlier flush (a render request's 0ms) back to a later one.
    const due = performance.now() + delayMs;
    if (renderTimeout != null && renderTimeoutDue <= due) return;
    clearTimeout(renderTimeout);
    renderTimeoutDue = due;
    renderTimeout = setTimeout(() => {
        renderTimeout = null;
        if (itemData.size == 0) {
            renderBusy = false;
            return;
        }
        renderBusy = true;

        // Claim only the requests received before this pass began. Anything arriving
        // while updatePositions runs keeps pendingRequestId ahead, so its awaiter waits
        // for the NEXT pass instead of being released by this one — which would not have
        // seen its changes. #c660
        const servedRequestId = pendingRequestId;
        lastServedRequestId = servedRequestId;

        const rT0 = performance.now();
        const updates = renderer.updatePositions();
        console.debug(`[RenderWorker] updatePositions: ${(performance.now() - rT0).toFixed(1)}ms, ${itemData.size} items, ${updates.size} changed`);
        pendingGeomIds.clear();

        const batchId = ++lastRenderBatchId;
        awaitingRenderAck = updates.size > 0;

        // see docs/EXTRACTED_COMMENTS.md#c800
        const encoded: Record<string, RenderEntry[]> = {};
        for (const [id, list] of updates) {
            encoded[id] = list.map((u): RenderEntry => [u.getUpdateType(), u.getValue()]);
        }

        // see docs/EXTRACTED_COMMENTS.md#c630
        (self as DedicatedWorkerGlobalScope).postMessage({
            type: "RENDER_UPDATE",
            batchId,
            servedRequestId,
            /* see docs/EXTRACTED_COMMENTS.md#c701 */
            version: acceptedVersion,
            updates: encoded,
        });
        renderBusy = false;
    }, delayMs);
}
